// Admin Media Manager — embedded into the product edit page.
// Allows upload (max 6 images / 2 videos), delete, and arrow-based reordering.
import React, { useRef, useState } from "react";
import { Trash2, Upload, ArrowLeft, ArrowRight, Loader2, ImageIcon, Video, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api/client";
import { resolveImage } from "../components/ProductCard";

const MAX_IMAGES = 6;
const MAX_VIDEOS = 2;

const Slot = ({ children, label }) => (
    <div className="aspect-square overflow-hidden rounded-xl border border-dashed border-zinc-200 bg-zinc-50/50">
        {children || (
            <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-zinc-300">
                <ImageIcon className="h-6 w-6" />
                <span className="text-[10px] uppercase tracking-wider">{label}</span>
            </div>
        )}
    </div>
);

const ProductMediaManager = ({ product, onUpdate }) => {
    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const images = product?.images || [];
    const videos = product?.videos || [];

    const uploadImage = async (file) => {
        if (!file) return;
        if (images.length >= MAX_IMAGES) {
            toast.error(`Max ${MAX_IMAGES} images`);
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Image must be ≤ 8MB");
            return;
        }
        setBusy(true);
        const form = new FormData();
        form.append("file", file);
        try {
            const { data } = await api.post(`/admin/products/${product.id}/images/upload`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Image uploaded & watermarked");
            onUpdate(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Upload failed");
        } finally { setBusy(false); }
    };

    const uploadVideo = async (file) => {
        if (!file) return;
        if (videos.length >= MAX_VIDEOS) {
            toast.error(`Max ${MAX_VIDEOS} videos`);
            return;
        }
        if (file.size > 50 * 1024 * 1024) {
            toast.error("Video must be ≤ 50MB");
            return;
        }
        setBusy(true);
        const form = new FormData();
        form.append("file", file);
        try {
            const { data } = await api.post(`/admin/products/${product.id}/videos/upload`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Video uploaded");
            onUpdate(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Upload failed");
        } finally { setBusy(false); }
    };

    const removeMedia = async (mediaType, index) => {
        if (!window.confirm(`Remove this ${mediaType}?`)) return;
        setBusy(true);
        try {
            const { data } = await api.delete(`/admin/products/${product.id}/media/${mediaType}/${index}`);
            toast.success(`${mediaType[0].toUpperCase() + mediaType.slice(1)} removed`);
            onUpdate(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Delete failed");
        } finally { setBusy(false); }
    };

    const reorderMedia = async (mediaType, fromIdx, toIdx) => {
        const arr = mediaType === "image" ? images : videos;
        if (toIdx < 0 || toIdx >= arr.length) return;
        const order = arr.map((_, i) => i);
        [order[fromIdx], order[toIdx]] = [order[toIdx], order[fromIdx]];
        setBusy(true);
        try {
            const body = mediaType === "image" ? { images: order } : { videos: order };
            const { data } = await api.patch(`/admin/products/${product.id}/media/reorder`, body);
            onUpdate(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Reorder failed");
        } finally { setBusy(false); }
    };

    return (
        <section className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft" data-testid="admin-product-media">
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <p className="label-uppercase">Media</p>
                    <h3 className="text-sm font-semibold text-zinc-900">Product images &amp; videos</h3>
                    <p className="text-[11px] text-zinc-500">Up to {MAX_IMAGES} images + {MAX_VIDEOS} videos. Auto-watermarked with the PILLS logo.</p>
                </div>
                {busy && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
            </div>

            {/* Images grid */}
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Images ({images.length} / {MAX_IMAGES})</p>
            <div className="mb-5 grid grid-cols-3 gap-3 sm:grid-cols-6">
                {Array.from({ length: MAX_IMAGES }).map((_, i) => {
                    const img = images[i];
                    if (!img) return <Slot key={i} label="empty" />;
                    return (
                        <div key={img.id || i} className="relative aspect-square overflow-hidden rounded-xl border border-zinc-100" data-testid={`admin-img-slot-${i}`}>
                            <img src={resolveImage(img)} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = "/pills-logo.jpg"; }} />
                            {i === 0 && (
                                <span className="absolute left-1 top-1 rounded-md bg-brand-700 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">Primary</span>
                            )}
                            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-gradient-to-t from-black/70 to-transparent p-1.5 opacity-0 transition-opacity hover:opacity-100">
                                <div className="flex gap-0.5">
                                    <button disabled={i === 0 || busy} onClick={() => reorderMedia("image", i, i - 1)} className="rounded p-1 text-white hover:bg-white/20 disabled:opacity-30" data-testid={`admin-img-left-${i}`}><ArrowLeft className="h-3 w-3" /></button>
                                    <button disabled={i === images.length - 1 || busy} onClick={() => reorderMedia("image", i, i + 1)} className="rounded p-1 text-white hover:bg-white/20 disabled:opacity-30" data-testid={`admin-img-right-${i}`}><ArrowRight className="h-3 w-3" /></button>
                                </div>
                                <button onClick={() => removeMedia("image", i)} className="rounded p-1 text-rose-300 hover:bg-rose-500/30" data-testid={`admin-img-delete-${i}`}><Trash2 className="h-3 w-3" /></button>
                            </div>
                            {img.is_legacy && (
                                <span className="absolute right-1 top-1 rounded-md bg-amber-100 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-amber-700" title="Legacy seed URL — not watermarked. Re-upload locally to apply watermark.">Seed</span>
                            )}
                        </div>
                    );
                })}
            </div>
            <button
                type="button"
                disabled={images.length >= MAX_IMAGES || busy}
                onClick={() => imageInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-4 py-2 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                data-testid="admin-img-upload-btn"
            >
                <Upload className="h-3 w-3" /> Upload image
            </button>
            <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="admin-img-upload-input" />

            {/* Videos grid */}
            <div className="mt-7 mb-2 flex items-center gap-2">
                <Video className="h-3.5 w-3.5 text-zinc-500" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Videos ({videos.length} / {MAX_VIDEOS})</p>
            </div>
            <div className="mb-3 grid grid-cols-2 gap-3 sm:max-w-md">
                {Array.from({ length: MAX_VIDEOS }).map((_, i) => {
                    const v = videos[i];
                    if (!v) return <Slot key={i} label="empty" />;
                    return (
                        <div key={v.id || i} className="relative aspect-square overflow-hidden rounded-xl border border-zinc-100 bg-black" data-testid={`admin-vid-slot-${i}`}>
                            {v.thumbnail_url ? (
                                <img src={resolveImage(v.thumbnail_url)} alt="" className="h-full w-full object-cover" />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center text-zinc-400">
                                    <Video className="h-6 w-6" />
                                </div>
                            )}
                            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-gradient-to-t from-black/70 to-transparent p-1.5 opacity-0 transition-opacity hover:opacity-100">
                                <div className="flex gap-0.5">
                                    <button disabled={i === 0 || busy} onClick={() => reorderMedia("video", i, i - 1)} className="rounded p-1 text-white hover:bg-white/20 disabled:opacity-30"><ArrowLeft className="h-3 w-3" /></button>
                                    <button disabled={i === videos.length - 1 || busy} onClick={() => reorderMedia("video", i, i + 1)} className="rounded p-1 text-white hover:bg-white/20 disabled:opacity-30"><ArrowRight className="h-3 w-3" /></button>
                                </div>
                                <button onClick={() => removeMedia("video", i)} className="rounded p-1 text-rose-300 hover:bg-rose-500/30" data-testid={`admin-vid-delete-${i}`}><Trash2 className="h-3 w-3" /></button>
                            </div>
                        </div>
                    );
                })}
            </div>
            <button
                type="button"
                disabled={videos.length >= MAX_VIDEOS || busy}
                onClick={() => videoInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-4 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                data-testid="admin-vid-upload-btn"
            >
                <Upload className="h-3 w-3" /> Upload video
            </button>
            <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => uploadVideo(e.target.files?.[0])} data-testid="admin-vid-upload-input" />

            <p className="mt-4 flex items-start gap-1.5 text-[10px] text-zinc-400">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                Videos up to 50MB · web-optimized mp4/webm/mov · first frame auto-extracted as thumbnail when possible.
            </p>
        </section>
    );
};

export default ProductMediaManager;
